import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import api from '../services/apiService';

const emptyForm = {
  companyName: '', contactName: '', email: '', phone: '', streetAddress: '',
  postalCode: '', city: '', country: 'DE', consentAccepted: false, website: '',
};

function formatCurrency(value, currency = 'EUR') {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(Number(value));
}

function formatDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  }).format(date);
}

export default function QualificationPage() {
  const { token } = useParams();
  const [metadata, setMetadata] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get(`/qualifications/${encodeURIComponent(token)}`)
      .then((response) => {
        setMetadata(response.data);
        if (response.data.prefill) setForm((current) => ({ ...current, ...response.data.prefill }));
      })
      .catch((requestError) => setError(requestError.response?.data?.error || 'Der Qualifizierungslink konnte nicht geprüft werden.'))
      .finally(() => setLoading(false));
  }, [token]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (!form.companyName.trim() || !form.contactName.trim() || !form.email.trim() || !form.phone.trim()
      || !form.streetAddress.trim() || !form.postalCode.trim() || !form.city.trim() || !form.country.trim()) {
      setError('Bitte alle Firmen- und Kontaktdaten vollständig ausfüllen.');
      return;
    }
    if (!form.consentAccepted) {
      setError('Bitte der Übermittlung Ihrer Firmendaten an den Vermieter zustimmen.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/qualifications/${encodeURIComponent(token)}`, form);
      setSuccess(true);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Die Firmendaten konnten nicht übermittelt werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="offer-shell">
      <Container>
        {loading ? <Spinner size="large" /> : success || metadata?.alreadyQualified ? (
          <Alert type="success" header="Vermieter ausgewählt">
            Ihre Firmendaten wurden sicher freigegeben. Der ausgewählte Vermieter erstellt nun auf Grundlage seiner Preisangaben ein qualifiziertes Vermieterangebot für Sie.
          </Alert>
        ) : !metadata ? (
          <Alert type="error" header="Link nicht verwendbar">{error || 'Der Qualifizierungslink ist ungültig oder abgelaufen.'}</Alert>
        ) : (
          <Form
            header={(
              <Header
                variant="h1"
                description={`Mietanfrage ${metadata.reference} · ${formatCurrency(metadata.priceNet, metadata.currency)}`}
              >
                Qualifiziertes Vermieterangebot erhalten
              </Header>
            )}
            errorText={error}
            actions={<Button variant="primary" loading={saving} onClick={submit}>Vermieter auswählen und Firmendaten übermitteln</Button>}
          >
            <form onSubmit={submit}>
              <SpaceBetween size="m">
                <Alert type="info">
                  Diese Preisinformation ist bis {formatDateTime(metadata.qualificationExpiresAt)} preislich gebunden. Mit der Übermittlung wählen Sie diesen Vermieter für ein qualifiziertes Vermieterangebot aus. Ihre Daten werden ausschließlich diesem Vermieter im SmartRent-Portal bereitgestellt.
                </Alert>
                <ColumnLayout columns={2} variant="text-grid">
                  <FormField label="Firmenname"><Input value={form.companyName} onChange={({ detail }) => setField('companyName', detail.value)} /></FormField>
                  <FormField label="Ansprechpartner"><Input value={form.contactName} onChange={({ detail }) => setField('contactName', detail.value)} /></FormField>
                  <FormField label="E-Mail"><Input type="email" value={form.email} onChange={({ detail }) => setField('email', detail.value)} /></FormField>
                  <FormField label="Telefon"><Input value={form.phone} onChange={({ detail }) => setField('phone', detail.value)} /></FormField>
                  <FormField label="Straße und Hausnummer"><Input value={form.streetAddress} onChange={({ detail }) => setField('streetAddress', detail.value)} /></FormField>
                  <FormField label="Postleitzahl"><Input value={form.postalCode} onChange={({ detail }) => setField('postalCode', detail.value)} /></FormField>
                  <FormField label="Ort"><Input value={form.city} onChange={({ detail }) => setField('city', detail.value)} /></FormField>
                  <FormField label="Land" description="Zweistelliges Länderkürzel"><Input value={form.country} onChange={({ detail }) => setField('country', detail.value.toUpperCase())} /></FormField>
                </ColumnLayout>
                <div className="honeypot" aria-hidden="true"><label>Webseite<Input value={form.website} onChange={({ detail }) => setField('website', detail.value)} /></label></div>
                <Checkbox checked={form.consentAccepted} onChange={({ detail }) => setField('consentAccepted', detail.checked)}>
                  Ich wähle den Vermieter dieses Preisangebots aus und stimme zu, dass ihm meine Firmen- und Kontaktdaten zur Angebotserstellung und Kontaktaufnahme bereitgestellt werden.
                </Checkbox>
              </SpaceBetween>
            </form>
          </Form>
        )}
      </Container>
    </div>
  );
}
